import React, { FC, ReactNode, createContext, useEffect } from 'react';
import { WidgetProps } from '../models/RequestAccessWidgetProps';
import { defaultSite } from '../models/RequestAccessModels';
import { getService } from '../services/requests';
import { RequestAccessGlobalWorkflowReducerStore } from '../models/RequestAccessStore';
import {
  providerInitialState,
  RequestAccessGlobalWorkflowType,
  useGlobalWorkflowReducer,
} from '../reducers/RequestAccessGlobalWorkflowReducer';
import {
  createErrorAction,
  createLoadingAction,
  createResetAction,
  createUpdateSiteAction,
  createUpdateLangAction,
  createOnBehalfOfAction,
  createSuccessAction,
} from '../reducers/RequestAccessGlobalWorkflowAction';
import { RequestsApi, RequestUsersApi } from 'services/ApiService/RequestAccessProxy/RequestAccessProxyApi';
import { PopsRequestProvider, RequestQuery, Service, SwaggerException } from 'services/ApiService/RequestAccessProxy/RequestAccessProxyApiClient';
import { PopsError } from '../models/RequestAccessPopsModels';

type RequestAccessGlobalWorkflowProviderState =
  RequestAccessGlobalWorkflowReducerStore &
  RequestAccessGlobalWorkflowType & {
    widgetProps: WidgetProps;
    hasOnBehalfOf: boolean;
  };

const initialState = {
  ...providerInitialState,
} as RequestAccessGlobalWorkflowProviderState;

export type RequestAccessGlobalWorkflowState = Readonly<RequestAccessGlobalWorkflowProviderState>;

const context = createContext<RequestAccessGlobalWorkflowState>(initialState);

const isValidSite = (site: Service): boolean =>
  !!site.name && !!site.serviceKey && (!!site.url || !!site.ressourceId);

const { Provider, Consumer } = context;

const retrieveAndEmitFullSite = (
  widgetProps: WidgetProps,
  dispatchSite: (site?: Service) => void,
): (() => void) => {
  let isMounted = true;
  getService(widgetProps).then((retrievedSite) => {
    if (isMounted) {
      dispatchSite(retrievedSite);
      widgetProps.emitGetService(retrievedSite);
    }
  });
  return () => {
    isMounted = false;
  };
};

const retrieveAndEmitSiteRessource = (
  widgetProps: WidgetProps,
  dispatchSite: (site?: Service) => void,
): (() => void) => {
  let isMounted = true;
  getService(widgetProps).then((retrievedSite) => {
    if (isMounted && widgetProps.site) {
      const newSite: Service = {
        ...widgetProps.site,
        resourceProfiles: retrievedSite.resourceProfiles,
        ressourceId: retrievedSite.ressourceId,
        activateIAMAccessWidget: retrievedSite.activateIAMAccessWidget,
      };
      dispatchSite(newSite);
      widgetProps.emitGetService(newSite);
    }
  });
  return () => {
    isMounted = false;
  };
};

const RequestAccessGlobalWorkflowProvider: FC<{
  widgetProps: WidgetProps;
  children?: ReactNode;
}> = ({ widgetProps, children }) => {
  const { ressourceId, popsIdentifier, url, site, icid: onBehalfOfIcId } = widgetProps;
  const [state, dispatch] = useGlobalWorkflowReducer();

  const hasOnBehalfOf = !!onBehalfOfIcId && onBehalfOfIcId.trim().length > 0;

  const dispatchSite = (site?: Service): void => {
    dispatch(createUpdateSiteAction(site));
  };

  useEffect(() => {
    const busValue = 'global.language';
    const updateLang = (lang: string | undefined): void => {
      dispatch(createUpdateLangAction(lang ?? 'en'));
    };
    const subscribeHandle = window.SGWTWidgetConfiguration.bus?.subscribe(busValue, updateLang);
    return () => {
      window.SGWTWidgetConfiguration.bus?.unsubscribe(subscribeHandle);
    };
  }, [window.SGWTWidgetConfiguration.bus]);

  useEffect(() => {
    if (site && isValidSite(site)) {
      dispatchSite(site);
      if ((!site.resourceProfiles || site.resourceProfiles.length === 0)
        && !site.popsIdentifier) {
        return retrieveAndEmitSiteRessource(
          widgetProps,
          dispatchSite,
        );
      }
      return;
    }
    if (popsIdentifier) {
      dispatchSite(undefined);
      return;
    }
    if (ressourceId || url)
      return retrieveAndEmitFullSite(
        widgetProps,
        dispatchSite,
      );
    dispatchSite(defaultSite);
    return;
  }, [ressourceId, popsIdentifier, url]);

  useEffect(() => {
    if (hasOnBehalfOf) {
      RequestUsersApi.getRequestUser(onBehalfOfIcId)
        .then(user => {
          dispatch(createOnBehalfOfAction(user.scope?.isInternal));
        })
        .catch(() => dispatch(createOnBehalfOfAction()));
    } else {
      dispatch(createOnBehalfOfAction());
    }
  }, [onBehalfOfIcId]);

  const manageRequestError = (error: unknown) => {
    let errorMessage = '';
    if (error instanceof SwaggerException) {
      if (error.status === 403) {
        errorMessage = `You are not authorized to request access to this application/profile in IAM.
              Please contact your IAM Analyst if you want to request access to this application/profile.
              Your IAM analysts are displayed in contacts tab in IAM.`;
      } else {
        let apiError: PopsError | null;
        try {
          apiError = JSON.parse(error.response) as PopsError;
        }
        catch {
          apiError = null;
        }
        if (error.status === 409 && apiError?.message === 'Resource already exists for user') {
          errorMessage = `You request access is already created in IAM.
            Please go to IAM to check the status.
            If it is validated, you will receive a confirmation email in the coming days.`;
        } else {
          errorMessage = apiError?.message || 'An error has occurred, please retry later';
        }
      }
    }
    else if (error instanceof Error) {
      errorMessage = error.message || 'An error has occurred, please retry later';
    }
    else {
      errorMessage = error as string;
    }
    dispatch(createErrorAction(errorMessage));
  };

  const sendRequestAccess = async (content: RequestQuery): Promise<void> => {
    dispatch(createLoadingAction());
    content.url = url;
    if (hasOnBehalfOf) {
      content.icId = onBehalfOfIcId;
    }

    try {
      await RequestsApi.createRequest(content);
      widgetProps.emitRequestSent(content);
      dispatch(createSuccessAction('Your request has been sent to the relevant team. if it is validated, you will receive a confirmation email in the coming days.'));
    } catch (error) {
      manageRequestError(error);
    }
  };

  const sendPopsRequestAccess = async (content: PopsRequestProvider): Promise<void> => {
    dispatch(createLoadingAction());

    try {
      await RequestsApi.createPopsRequest(content);
      widgetProps.emitRequestSent(content);
      dispatch(createSuccessAction('Your access request has been created in IAM and the validation process is in progress. If it is validated, you will receive a confirmation email in the coming days.'));
    } catch (error) {
      manageRequestError(error);
    }
  };

  const resetRequestAccess = (): void => dispatch(createResetAction());

  return <Provider
    value={{
      ...state,
      hasOnBehalfOf,
      widgetProps,
      sendRequestAccess: sendRequestAccess,
      sendPopsRequestAccess: sendPopsRequestAccess,
      resetRequestAccess: resetRequestAccess,
    }}
  >
    {children}
  </Provider>;
};

export {
  RequestAccessGlobalWorkflowProvider,
  Consumer as RequestAccessGlobalWorkflowConsumer,
  context as RequestAccessGlobalWorkflowContext,
};
